import React, { useState } from 'react';
import { Container, Grid, CircularProgress, Typography, Box, Button } from '@mui/material';
import moment from 'moment';
import Page from 'src/components/Page';
import { useNavigate, useParams } from 'react-router-dom';

import { IoClose } from 'react-icons/io5';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { VCurrencyIcon, HandHoldingUsdIcon } from 'src/images';
import 'react-toastify/dist/ReactToastify.css';
import palette from 'src/theme/palette';
import { financialSimulationService } from 'src/services/financial-simulation.service';
import { studentService } from 'src/services/student.service';
import { CalenderIcon, LoanIcon } from 'src/images/svg';
import { HiOutlineArrowRight } from 'react-icons/hi';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/context/auth/AuthContext';
import '../../styles/viewstudent.css';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export default function StudentViewFinancialSimulations(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams();
  const [open, setOpen] = useState<boolean>(false);
  const { user } = useAuth();

  const {
    data: financialSimulation,
    isLoading: isLoadingSimulation,
    error: simulationError,
  } = useQuery({
    queryKey: ['financialSimulation', id],
    queryFn: () => financialSimulationService.getChallengeById(id),
    enabled: !!id,
  });

  const { data: studentData, isLoading: isLoadingStudentId } = useQuery({
    queryKey: ['studentId', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id || ''),
    enabled: !!user?.id,
  });

  const { data: financialSummary, isLoading: isLoadingFinancialSummary } = useQuery({
    queryKey: ['financialSummary', studentData],
    queryFn: () => studentService.getStudentFinancialSummary(studentData || ''),
    enabled: !!studentData,
  });

  const scenario = financialSimulation?.scenario.title;
  const status = financialSimulation?.status;
  const duration = financialSimulation?.duration;
  const amount = financialSimulation?.scenario?.amount;
  const startDate = financialSimulation?.createdat;
  const endDate = financialSimulation?.createdat;
  const student = financialSimulation?.student;
  const loanRequest = financialSimulation?.loan;
  const description = financialSimulation?.scenario?.description;
  const currentBalance = financialSummary?.currentBalance;

  const isLoading = isLoadingSimulation || isLoadingStudentId || isLoadingFinancialSummary;

  if (isLoading) {
    return (
      <Page title="Users">
        <Container maxWidth={false}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress color="primary" />
          </Box>
        </Container>
      </Page>
    );
  }

  if (simulationError) {
    return (
      <Page title="Users">
        <Container maxWidth={false}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Typography variant="h5" color="textSecondary">
              Error loading financial simulation data.
            </Typography>
          </Box>
        </Container>
      </Page>
    );
  }

  if (!financialSimulation) {
    return (
      <Page title="Users">
        <Container maxWidth={false}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Typography variant="h5" color="textSecondary">
              Financial Simulation not found
            </Typography>
          </Box>
        </Container>
      </Page>
    );
  }

  const canPayWithBalance = currentBalance !== undefined && Number(amount) <= currentBalance;

  return (
    <Page title="Users">
      <Container maxWidth={false}>
        <div className="profileDetails">
          <div>
            <div className="detailsRow1">
              <Typography variant="h3">{scenario}</Typography>
              <div
                className={`_loan_status ${
                  status === 'In Progress'
                    ? 'status_pending'
                    : status === 'Overdue'
                    ? 'status_denied'
                    : status === 'Completed'
                    ? 'status_approved'
                    : ''
                }`}
                style={{ marginLeft: 10, padding: '0px 15px' }}
              >
                <Typography variant="subtitle2">{status}</Typography>
              </div>
            </div>
            <div className="detailsRow2">
              <div>
                <Typography color={palette.grey[600]} variant="subtitle2">
                  Description
                </Typography>
                <Typography variant="subtitle1">{description}</Typography>
              </div>
              <div style={{ marginLeft: 20 }}>
                <Typography color={palette.grey[600]} variant="subtitle2">
                  Duration
                </Typography>
                <Typography variant="subtitle1">{duration}</Typography>
              </div>
            </div>
          </div>
        </div>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12} md={6} lg={6} xl={6}>
            <div className="_payment_card">
              <div>
                <Typography variant="h3">{amount} $D</Typography>
                <Typography variant="caption">Amount Required</Typography>
              </div>
              <div className="_card_icon_view" style={{ backgroundColor: palette.secondary.main }}>
                <img src={VCurrencyIcon} alt="VCurrencyIcon" />
              </div>
            </div>
          </Grid>
          <Grid item sm={6} xs={12} md={6} lg={6} xl={6}>
            <div className="_payment_card">
              <div>
                <Typography variant="h3" color={!canPayWithBalance ? palette.error.main : palette.primary.main}>
                  {currentBalance} $D
                </Typography>
                <Typography variant="caption">{status === 'Completed' ? 'Paid Amount' : 'Current Balance'}</Typography>
              </div>
              <div
                className="_card_icon_view"
                style={{ backgroundColor: status === 'Completed' ? palette.primary.main : palette.error.main }}
              >
                <img src={VCurrencyIcon} alt="CreditIcon" />
              </div>
            </div>
          </Grid>
          <Grid item sm={12} xs={12} md={12} lg={12} xl={12}>
            <div className="_payment_card2" style={{ marginTop: 0 }}>
              <div className="statusDetails">
                <div className="_card_icon_view" style={{ backgroundColor: palette.secondary.main }}>
                  <CalenderIcon color={palette.common.white} />
                </div>
                <Typography variant="caption" style={{ marginTop: 10 }}>
                  Started On
                </Typography>
                <Typography variant="h6">{moment(startDate).format('DD/MM/YYYY')} | 12:00</Typography>
              </div>

              {loanRequest && (
                <>
                  <div className="statusDetails">
                    <div className="_card_icon_view" style={{ backgroundColor: palette.secondary.main }}>
                      <LoanIcon color={palette.common.white} />
                    </div>
                    <Typography variant="caption" style={{ marginTop: 10 }}>
                      Loan requested
                    </Typography>
                    <Typography variant="h6">{moment(loanRequest.createdat).format('DD/MM/YYYY')} | 12:00</Typography>
                  </div>
                  {loanRequest.status === 'approved' && (
                    <div className="statusDetails">
                      <div className="_card_icon_view" style={{ backgroundColor: palette.primary.main }}>
                        <LoanIcon color={palette.common.white} />
                      </div>
                      <Typography variant="caption" style={{ marginTop: 10 }}>
                        Loan Approved
                      </Typography>
                      <Typography variant="h6">{moment(loanRequest.updatedat).format('DD/MM/YYYY')} | 12:00</Typography>
                    </div>
                  )}
                </>
              )}
              <div className="statusDetails">
                <div
                  className="_card_icon_view"
                  style={{ backgroundColor: status === 'Completed' ? palette.primary.main : palette.error.main }}
                >
                  {status === 'Completed' ? (
                    <img src={VCurrencyIcon} alt="CreditIcon" />
                  ) : (
                    <CalenderIcon color={palette.common.white} />
                  )}
                </div>
                <Typography
                  variant="caption"
                  color={status === 'Completed' ? palette.primary.main : undefined}
                  style={{ marginTop: 10 }}
                >
                  {status === 'Completed' ? 'Completed' : status === 'Overdue' ? 'Overdue' : 'Ends On'}
                </Typography>
                <Typography variant="h6">{moment(endDate).format('DD/MM/YYYY')} | 12:00</Typography>
              </div>
            </div>
          </Grid>
          {status !== 'Completed' && status !== 'Overdue' && (
            <>
              <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
                <div
                  className="takeaLoanCard"
                  style={{
                    backgroundColor:
                      loanRequest || loanRequest?.status === 'approved' ? palette.grey[700] : palette.secondary.main,
                    cursor: loanRequest || loanRequest?.status === 'approved' ? 'auto' : 'pointer',
                  }}
                  onClick={() =>
                    loanRequest || loanRequest?.status === 'approved'
                      ? undefined
                      : navigate('/student/new-loan-request')
                  }
                >
                  <img src={HandHoldingUsdIcon} alt="HandHoldingUsdIcon" />
                  <Typography variant="h6" color={palette.common.white} style={{ flex: 1, marginLeft: 20 }}>
                    Take a Loan
                  </Typography>
                  <HiOutlineArrowRight color={palette.common.white} size={25} />
                </div>
              </Grid>
              <Grid item sm={12} xs={12} md={6} lg={6} xl={6}>
                <div
                  className="takeaLoanCard"
                  style={{
                    backgroundColor: !canPayWithBalance ? palette.grey[700] : palette.primary.main,
                    cursor: !canPayWithBalance ? 'auto' : 'pointer',
                  }}
                  onClick={() => (canPayWithBalance ? setOpen(true) : undefined)}
                >
                  <img src={VCurrencyIcon} alt="VCurrencyIcon" width={38} height={38} />
                  <Typography variant="h6" color={palette.common.white} style={{ flex: 1, marginLeft: 20 }}>
                    Pay with Balance
                  </Typography>
                  <HiOutlineArrowRight color={palette.common.white} size={25} />
                </div>
              </Grid>
            </>
          )}
        </Grid>

        {/* Payment Confirmation Modal */}
        <BootstrapDialog aria-labelledby="customized-dialog-title" open={open} maxWidth="xs" fullWidth>
          <div className="_user_modal_header">
            <div></div>
            <span onClick={() => setOpen(false)}>
              <IoClose size={25} color={palette.grey[500]} />
            </span>
          </div>

          <DialogContent>
            <div className="_user_modal_body" style={{ padding: '0px 20px' }}>
              <div
                className="_card_icon_view"
                style={{ backgroundColor: palette.secondary.main, width: 120, height: 120 }}
              >
                <img src={VCurrencyIcon} alt="VCurrencyIcon" width={60} />
              </div>
              <Typography variant="h2" color={palette.text.primary} style={{ marginTop: 20 }}>
                Are You Sure?
              </Typography>
              <Typography
                variant="subtitle2"
                color={palette.grey[600]}
                style={{ fontSize: 16, marginTop: 10, textAlign: 'center' }}
              >
                Are you sure you want to proceed with this payment? Please confirm to continue.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                style={{ height: 55, marginTop: 20, marginBottom: 10 }}
                onClick={() => setOpen(false)}
              >
                Pay
              </Button>
            </div>
          </DialogContent>
        </BootstrapDialog>
      </Container>
    </Page>
  );
}
